import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom-v5-compat';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { RangeInput } from 'shared/components/FormControl';
import { FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { FileUpload } from 'dashboard/components/FormControls/FormControl';
import InputField from 'dashboard/components/FormControls/InputField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import ThemeEditorFormActionFooter, {
  ACTION_FOOTER_HEIGHT,
} from 'dashboard/components/ThemeEditorFormActionFooter';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { FORM_THEME_EDITOR_START_PAGE_NAME } from 'dashboard/lib/constants/formNames';
import { COLORS } from 'dashboard/styles/variables';

const Title = styled('h2')`
  color: ${COLORS.black};
  padding-top: ${SPACINGS.base * 5}px;
  padding-left: ${SPACINGS.base * 2}px;
`;
const Subtitle = styled('p')`
  display: inline-block;
  border-bottom: 1px solid ${COLORS.gray20};
  padding-bottom: ${SPACINGS.base * 3}px;
  padding-left: ${SPACINGS.base * 2}px;
  padding-right: ${SPACINGS.base * 2}px;
`;
const Section = styled('div')`
  padding: ${SPACINGS.base * 2}px;
  margin-bottom: ${ACTION_FOOTER_HEIGHT};
`;
const StyledLabel = styled('label')`
  display: inline-block;
`;
const Hint = styled('p')`
  font-size: ${FONT_SIZES.small};
  color: ${COLORS.gray50};
  margin-bottom: ${SPACINGS.base}px;
`;
const StyledField = styled(Field)<any>`
  margin-bottom: ${SPACINGS.base * 3}px;
`;

const FILE_UPLOAD_LABEL = (
  <>
    <p>Drag and drop your logo here</p>
    <p>PNG, GIF or JPEG only</p>
  </>
);

interface FormThemeEditorStartPageProps {
  currentLogoUrl?: string;
}

const FormThemeEditorStartPage = (props: FormThemeEditorStartPageProps & InjectedFormProps) => {
  const [t] = useTranslation();
  return (
    <form onSubmit={props.handleSubmit}>
      <Title>Start Page</Title>
      <Subtitle>
        Custom text in the flow will not be translated unless custom translation file(s) are added.
        <Link to='/dashboard/contact-us'>Contact us</Link> to add your custom file(s).
      </Subtitle>
      <Section>
        <StyledField
          name='theme.logo-file'
          component={FileUpload}
          accept={['image/png', 'image/gif', 'image/jpeg']}
          label={FILE_UPLOAD_LABEL}
          labelDrag={FILE_UPLOAD_LABEL}
          onChange={(file?: FileWithPreview) => props.change('theme.logo-url', file?.preview ?? '')}
          preview={
            props.currentLogoUrl
              ? {
                  isImage: true,
                  preview: props.currentLogoUrl,
                }
              : null
          }
        />

        <StyledLabel htmlFor='start-page-form__logo-width-input'>Logo width</StyledLabel>
        <StyledField
          id='start-page-form__logo-width-input'
          name='theme.logo-width'
          component={RangeInput}
          min={50}
          max={352}
          unit='px'
          format={(value: string) => (value ? value.substr(0, value.length - 2) : '')}
          normalize={(value: number) => `${value}px`}
        />

        <StyledLabel htmlFor='start-page-theme-form__title-input'>Title</StyledLabel>
        <Hint>Title for start page (only displayed if no logo)</Hint>
        <StyledField
          type='text'
          placeholder={t('intro.title')}
          id='start-page-theme-form__title-input'
          name='template.intro-title'
          component={InputField}
        />

        <StyledLabel htmlFor='start-page-theme-form__body-input'>Body</StyledLabel>
        <Hint>Body for start page</Hint>
        <StyledField
          id='start-page-theme-form__body-input'
          placeholder={t('intro.prompt')}
          name='template.intro-prompt'
          component={TextAreaField}
        />

        <StyledLabel htmlFor='start-page-theme-form__button-label-input'>Button label</StyledLabel>
        <Hint>Button label for start page</Hint>
        <StyledField
          type='text'
          id='start-page-theme-form__button-label-input'
          placeholder={t('intro.btn-submit')}
          name='template.intro-btn-submit'
          component={InputField}
        />
      </Section>

      <ThemeEditorFormActionFooter
        dataTestBlock='form-theme-editor-start-page'
        handleUndo={props.reset}
        submitting={props.submitting}
        pristine={props.pristine}
      />
    </form>
  );
};

export default reduxForm<{}, FormThemeEditorStartPageProps>({
  form: FORM_THEME_EDITOR_START_PAGE_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(FormThemeEditorStartPage);
